/**
 * Activity Resolution
 * Resolves all committed activities for a stretch turn.
 * Mutates a cloned state for performance.
 */

import {
  computeVisibility,
  updateFog,
  evaluateStepTriggers,
  evaluateProximityTriggers,
  evaluateInteractTriggers,
  applyTriggerEvents,
  coordKey,
} from '@omen-quest/grid-map';
import type { TriggerEvent } from '@omen-quest/grid-map';

import type {
  StretchState,
  StretchCommit,
  ActivityResult,
} from './types';
import { SEARCH_RADIUS, EXPLORE_VISION_MULTIPLIER, STEALTH_SPEED_MULTIPLIER } from './types';
import { computePlayerVision } from './crawl-strategy';

// ============================================================================
// MAIN RESOLUTION
// ============================================================================

/** Resolve all commits for the current stretch. Mutates and returns a cloned state. */
export function resolveCommits(state: StretchState): StretchState {
  const next = cloneState(state);
  const results: ActivityResult[] = [];

  // Pre-pass: identify help bonuses
  const helpMap = new Map<string, string>(); // targetPlayerId → helperId
  for (const commit of next.commits) {
    if (commit.activity.type === 'help') {
      helpMap.set(commit.activity.targetPlayerId, commit.playerId);
    }
  }

  // Attach help bonuses
  for (const commit of next.commits) {
    if (helpMap.has(commit.playerId)) {
      commit.helpBonusFrom = helpMap.get(commit.playerId);
    }
  }

  // Resolve each commit
  for (const commit of next.commits) {
    const result = resolveActivity(next, commit);
    results.push(result);
  }

  // Build resolution record
  next.lastResolution = {
    stretchNumber: next.stretchNumber,
    activityResults: results,
    timelineEventsFired: [],
    wanderingMonster: { checked: false, triggered: false },
    countdownsExpired: [],
    lightSourcesDepleted: [],
  };

  return next;
}

// ============================================================================
// PER-ACTIVITY RESOLUTION
// ============================================================================

function resolveActivity(state: StretchState, commit: StretchCommit): ActivityResult {
  const { playerId, activity } = commit;
  const result: ActivityResult = {
    playerId,
    activityType: activity.type,
    tilesRevealed: 0,
    itemsFound: [],
    triggerEvents: [],
    notes: [],
  };

  switch (activity.type) {
    case 'move':
      resolveMove(state, playerId, activity.path, result);
      break;
    case 'search':
      resolveSearch(state, playerId, result, !!commit.helpBonusFrom);
      break;
    case 'explore':
      resolveExplore(state, playerId, activity.path, result);
      break;
    case 'keep_watch':
      resolveKeepWatch(state, playerId, result);
      break;
    case 'move_quietly':
      resolveMoveQuietly(state, playerId, activity.path, result);
      break;
    case 'interact':
      resolveInteract(state, playerId, activity.x, activity.y, result);
      break;
    case 'help':
      resolveHelp(state, playerId, activity.targetPlayerId, result);
      break;
    case 'loot':
      resolveLoot(state, playerId, result);
      break;
    case 'cast_spell':
      resolveCastSpell(state, playerId, result);
      break;
    case 'take_your_time':
      resolveTakeYourTime(state, playerId, activity.description, result);
      break;
    case 'wait':
      result.notes.push('Waited.');
      break;
  }

  return result;
}

// ============================================================================
// MOVEMENT
// ============================================================================

function resolveMove(
  state: StretchState,
  playerId: string,
  path: Array<{ x: number; y: number }>,
  result: ActivityResult,
): void {
  const playerIdx = state.players.findIndex(p => p.id === playerId);
  if (playerIdx === -1) return;
  const player = state.players[playerIdx];

  movePlayerAlongPath(state, playerIdx, path, result);
  result.notes.push(`Moved to (${player.x}, ${player.y}).`);
}

function resolveExplore(
  state: StretchState,
  playerId: string,
  path: Array<{ x: number; y: number }> | undefined,
  result: ActivityResult,
): void {
  const playerIdx = state.players.findIndex(p => p.id === playerId);
  if (playerIdx === -1) return;
  const player = state.players[playerIdx];

  // Move at half speed if path provided
  if (path && path.length > 0) {
    const halfBudget = Math.floor(player.movementBudget * 0.5);
    const clampedPath = path.slice(0, halfBudget + 1);
    movePlayerAlongPath(state, playerIdx, clampedPath, result);
  }

  // Expanded vision
  const baseRadius = computePlayerVision(
    player.visionRadius,
    state.crawlStrategy.lightSources,
    playerId,
  );
  const expandedRadius = Math.ceil(baseRadius * EXPLORE_VISION_MULTIPLIER);
  updatePlayerVisibility(state, playerIdx, expandedRadius);
  result.notes.push('Explored cautiously with expanded vision.');
}

function resolveMoveQuietly(
  state: StretchState,
  playerId: string,
  path: Array<{ x: number; y: number }> | undefined,
  result: ActivityResult,
): void {
  const playerIdx = state.players.findIndex(p => p.id === playerId);
  if (playerIdx === -1) return;
  const player = state.players[playerIdx];

  player.isStealthed = true;

  if (path && path.length > 0) {
    const halfBudget = Math.floor(player.movementBudget * STEALTH_SPEED_MULTIPLIER);
    const clampedPath = path.slice(0, halfBudget + 1);
    movePlayerAlongPath(state, playerIdx, clampedPath, result);
  }

  result.notes.push('Moving quietly (stealthed).');
}

// ============================================================================
// SEARCH & INTERACT
// ============================================================================

function resolveSearch(
  state: StretchState,
  playerId: string,
  result: ActivityResult,
  hasHelp: boolean,
): void {
  const player = state.players.find(p => p.id === playerId);
  if (!player) return;

  const perception = player.passivePerception + (hasHelp ? 5 : 0);

  // Check for hidden triggers within search radius
  const triggers = state.map.triggers ?? [];
  for (const trigger of triggers) {
    if (!trigger.visible && trigger.condition === 'interact') {
      for (const cell of trigger.cells) {
        const dist = Math.abs(cell.x - player.x) + Math.abs(cell.y - player.y);
        if (dist <= SEARCH_RADIUS) {
          // Use default DC 15 for hidden triggers; custom effects can store DC in data
          const dc = trigger.effect.type === 'custom'
            ? ((trigger.effect.data?.dc as number) ?? 15)
            : 15;
          if (perception >= dc) {
            result.notes.push(`Discovered hidden: ${trigger.id}`);
            result.triggerEvents.push({
              triggerId: trigger.id,
              effect: { type: 'custom', eventName: 'discovered', data: { triggerId: trigger.id } },
              sourceCell: { x: cell.x, y: cell.y },
              playerPosition: { x: player.x, y: player.y },
            });
          }
        }
      }
    }
  }

  // Check for hidden entities
  const entities = state.map.entities;
  for (const entity of entities) {
    if (entity.metadata?.hidden) {
      const dist = Math.abs(entity.x - player.x) + Math.abs(entity.y - player.y);
      if (dist <= SEARCH_RADIUS) {
        const dc = (entity.metadata.hiddenDC as number) ?? 15;
        if (perception >= dc) {
          result.notes.push(`Found: ${entity.name}`);
          result.itemsFound.push(entity.id);
        }
      }
    }
  }

  if (result.notes.length === 0) {
    result.notes.push('Searched the area — nothing found.');
  }
}

function resolveInteract(
  state: StretchState,
  playerId: string,
  targetX: number,
  targetY: number,
  result: ActivityResult,
): void {
  const player = state.players.find(p => p.id === playerId);
  if (!player) return;

  const triggers = state.map.triggers ?? [];
  const events = evaluateInteractTriggers(
    triggers,
    state.firedTriggers,
    state.triggerFlags,
    targetX,
    targetY,
    player.x,
    player.y,
  );

  if (events.length > 0) {
    // Apply trigger events to state
    const tempState = applyTriggerEvents(
      {
        map: state.map,
        playerId,
        fogGrid: player.fogGrid,
        lightGrid: player.lightGrid,
        openDoors: state.openDoors,
        lockedDoors: new Set<string>(),
        firedTriggers: state.firedTriggers,
        triggerFlags: state.triggerFlags,
        pendingEvents: [],
        moveCount: 0,
      },
      events,
    );
    // Propagate trigger state changes back
    state.firedTriggers = tempState.firedTriggers;
    state.triggerFlags = tempState.triggerFlags;
    state.map = tempState.map;
    result.triggerEvents.push(...events);
    result.notes.push(`Interacted at (${targetX}, ${targetY}).`);

    // Check for combat triggers
    for (const event of events) {
      if (event.effect.type === 'combat') {
        state.outcome = 'combat_triggered';
        result.notes.push('Combat triggered!');
      }
    }
  } else {
    result.notes.push(`Nothing to interact with at (${targetX}, ${targetY}).`);
  }
}

// ============================================================================
// STATUS ACTIVITIES
// ============================================================================

function resolveKeepWatch(
  state: StretchState,
  playerId: string,
  result: ActivityResult,
): void {
  const player = state.players.find(p => p.id === playerId);
  if (!player) return;
  player.isKeepingWatch = true;
  result.notes.push('Keeping watch — surprise prevention active.');
}

function resolveHelp(
  state: StretchState,
  _playerId: string,
  targetPlayerId: string,
  result: ActivityResult,
): void {
  const target = state.players.find(p => p.id === targetPlayerId);
  if (!target) return;
  result.notes.push(`Helping ${target.name} — they receive a bonus.`);
}

function resolveLoot(
  state: StretchState,
  playerId: string,
  result: ActivityResult,
): void {
  const player = state.players.find(p => p.id === playerId);
  if (!player) return;

  // Find lootable entities nearby
  for (const entity of state.map.entities) {
    if (entity.type === 'object' && entity.metadata?.lootable) {
      const dist = Math.abs(entity.x - player.x) + Math.abs(entity.y - player.y);
      if (dist <= 1) {
        result.itemsFound.push(entity.id);
        result.notes.push(`Looted: ${entity.name}`);
      }
    }
  }

  if (result.itemsFound.length === 0) {
    result.notes.push('Nothing to loot nearby.');
  }
}

function resolveCastSpell(
  _state: StretchState,
  _playerId: string,
  result: ActivityResult,
): void {
  // Spell resolution is AI-driven — engine just marks time spent
  result.notes.push('Casting spell (AI resolves effect).');
}

function resolveTakeYourTime(
  _state: StretchState,
  _playerId: string,
  description: string,
  result: ActivityResult,
): void {
  // Auto-succeed on a task — AI narrates the outcome
  result.notes.push(`Taking time: ${description} (auto-success).`);
}

// ============================================================================
// MOVEMENT HELPERS
// ============================================================================

/** Move player along a validated path, evaluating step/proximity triggers along the way. */
function movePlayerAlongPath(
  state: StretchState,
  playerIdx: number,
  path: Array<{ x: number; y: number }>,
  result: ActivityResult,
): void {
  const player = state.players[playerIdx];

  for (const step of path) {
    // Auto-open closed doors
    const cell = state.map.cells[step.y]?.[step.x];
    if (cell?.terrain === 'door') {
      const key = coordKey(step.x, step.y);
      if (!state.openDoors.has(key)) {
        state.openDoors.add(key);
        result.notes.push(`Opened door at (${step.x}, ${step.y}).`);
      }
    }

    player.x = step.x;
    player.y = step.y;

    // Evaluate step triggers
    const triggers = state.map.triggers ?? [];
    const stepEvents = evaluateStepTriggers(
      triggers,
      state.firedTriggers,
      state.triggerFlags,
      player.x,
      player.y,
    );
    const proxEvents = evaluateProximityTriggers(
      triggers,
      state.firedTriggers,
      state.triggerFlags,
      player.x,
      player.y,
    );

    const allEvents = [...stepEvents, ...proxEvents];
    if (allEvents.length > 0) {
      applyTriggerEventsToState(state, player.id, allEvents);
      result.triggerEvents.push(...allEvents);

      // Check for combat triggers — stop movement
      for (const event of allEvents) {
        if (event.effect.type === 'combat') {
          state.outcome = 'combat_triggered';
          result.notes.push('Combat triggered during movement!');
          break;
        }
      }
      if (state.outcome === 'combat_triggered') break;
    }
  }

  // Update visibility at final position
  updatePlayerVisibility(
    state,
    playerIdx,
    computePlayerVision(player.visionRadius, state.crawlStrategy.lightSources, player.id),
  );
}

/** Update a player's fog/light grids from their current position. */
function updatePlayerVisibility(
  state: StretchState,
  playerIdx: number,
  radius: number,
): void {
  const player = state.players[playerIdx];
  if (radius <= 0) return;

  const vis = computeVisibility(state.map, player.x, player.y, radius, state.openDoors);
  updateFog(player.fogGrid, player.lightGrid, vis);
}

/** Apply trigger events back to shared state. */
function applyTriggerEventsToState(
  state: StretchState,
  playerId: string,
  events: TriggerEvent[],
): void {
  const player = state.players.find(p => p.id === playerId)!;
  const tempState = applyTriggerEvents(
    {
      map: state.map,
      playerId,
      fogGrid: player.fogGrid,
      lightGrid: player.lightGrid,
      openDoors: state.openDoors,
      lockedDoors: new Set<string>(),
      firedTriggers: state.firedTriggers,
      triggerFlags: state.triggerFlags,
      pendingEvents: [],
      moveCount: 0,
    },
    events,
  );
  state.firedTriggers = tempState.firedTriggers;
  state.triggerFlags = tempState.triggerFlags;
  state.map = tempState.map;
}

// ============================================================================
// STATE CLONING
// ============================================================================

/** Deep clone state, preserving Set instances. */
function cloneState(state: StretchState): StretchState {
  const cloned = structuredClone(state) as StretchState;
  // structuredClone handles Sets in modern engines, but be safe
  cloned.openDoors = new Set(state.openDoors);
  cloned.firedTriggers = new Set(state.firedTriggers);
  cloned.triggerFlags = new Set(state.triggerFlags);
  return cloned;
}
